import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { elements, categoryLabels, type Element, type ElementCategory } from "@/data/elements";
import ElementTile, { type ColorMode } from "./ElementTile";
import ElementDetailPanel from "./ElementDetailPanel";
import GlobalSearch from "./GlobalSearch";

const categories: ElementCategory[] = [
  "nonmetal", "noble-gas", "alkali-metal", "alkaline-earth",
  "metalloid", "halogen", "transition-metal", "post-transition-metal",
  "lanthanide", "actinide", "unknown",
];

const categoryColorMap: Record<ElementCategory, string> = {
  "alkali-metal": "bg-cat-alkali",
  "alkaline-earth": "bg-cat-alkaline",
  "transition-metal": "bg-cat-transition",
  "post-transition-metal": "bg-cat-post-transition",
  "metalloid": "bg-cat-metalloid",
  "nonmetal": "bg-cat-nonmetal",
  "halogen": "bg-cat-halogen",
  "noble-gas": "bg-cat-noble",
  "lanthanide": "bg-cat-lanthanide",
  "actinide": "bg-cat-actinide",
  "unknown": "bg-cat-unknown",
};

const colorModes: { key: ColorMode; label: string }[] = [
  { key: "category", label: "Category" },
  { key: "phase", label: "Phase" },
  { key: "block", label: "Block" },
  { key: "electronegativity", label: "Electronegativity" },
  { key: "radius", label: "Atomic Radius" },
  { key: "discovery", label: "Discovery Era" },
];

const phaseLegend = [
  { label: "Solid", color: "hsl(25, 85%, 50%)" },
  { label: "Liquid", color: "hsl(200, 80%, 50%)" },
  { label: "Gas", color: "hsl(120, 50%, 45%)" },
  { label: "Unknown", color: "hsl(220, 10%, 50%)" },
];

const blockLegend = [
  { label: "s-block", color: "hsl(4, 80%, 55%)" },
  { label: "p-block", color: "hsl(210, 80%, 55%)" },
  { label: "d-block", color: "hsl(45, 85%, 50%)" },
  { label: "f-block", color: "hsl(120, 45%, 45%)" },
];

function GradientLegend({ lowLabel, highLabel, lowColor, highColor }: { lowLabel: string; highLabel: string; lowColor: string; highColor: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">{lowLabel}</span>
      <div className="w-32 h-2.5 rounded-full" style={{ background: `linear-gradient(to right, ${lowColor}, ${highColor})` }} />
      <span className="text-xs text-muted-foreground">{highLabel}</span>
    </div>
  );
}

export default function PeriodicTable() {
  const [selectedElement, setSelectedElement] = useState<Element | null>(null);
  const [hoverCategory, setHoverCategory] = useState<ElementCategory | null>(null);
  const [lockedCategory, setLockedCategory] = useState<ElementCategory | null>(null);
  const [colorMode, setColorMode] = useState<ColorMode>("category");
  const [focusedNumber, setFocusedNumber] = useState<number | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const highlightCategory = colorMode === "category" ? (hoverCategory ?? lockedCategory) : null;

  const handleClick = useCallback((el: Element) => {
    setSelectedElement(el);
  }, []);

  const toggleLock = (cat: ElementCategory) => {
    setLockedCategory((prev) => (prev === cat ? null : cat));
  };

  // Build a grid map for keyboard navigation
  const gridMap = useMemo(() => {
    const map: Record<string, number> = {};
    elements.forEach((el) => {
      map[`${el.row},${el.col}`] = el.number;
    });
    return map;
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!focusedNumber) return;
      const current = elements.find((el) => el.number === focusedNumber);
      if (!current) return;

      let newRow = current.row;
      let newCol = current.col;

      if (e.key === "ArrowRight") newCol++;
      else if (e.key === "ArrowLeft") newCol--;
      else if (e.key === "ArrowDown") newRow++;
      else if (e.key === "ArrowUp") newRow--;
      else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setSelectedElement(current);
        return;
      } else return;

      e.preventDefault();

      // Find nearest element in new position
      for (let attempt = 0; attempt < 18; attempt++) {
        const key = `${newRow},${newCol + attempt}`;
        const keyNeg = `${newRow},${newCol - attempt}`;
        if (gridMap[key]) { setFocusedNumber(gridMap[key]); return; }
        if (attempt > 0 && gridMap[keyNeg]) { setFocusedNumber(gridMap[keyNeg]); return; }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [focusedNumber, gridMap]);

  // Focus the first element on Tab into grid
  const handleGridFocus = () => {
    if (!focusedNumber) setFocusedNumber(1);
  };

  return (
    <div className="w-full max-w-[1200px] mx-auto px-4">
      {/* Global Search */}
      <GlobalSearch onSelect={handleClick} />

      {/* Color mode selector */}
      <div className="flex flex-wrap items-center gap-2 mb-3 justify-center">
        <span className="text-xs text-muted-foreground mr-1">Color by:</span>
        {colorModes.map((m) => (
          <button
            key={m.key}
            onClick={() => {
              setColorMode(m.key);
              setLockedCategory(null);
              setHoverCategory(null);
            }}
            className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
              colorMode === m.key
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 mb-4 justify-center items-center">
        {colorMode === "category" &&
          categories.map((cat) => (
            <button
              key={cat}
              onClick={() => toggleLock(cat)}
              onMouseEnter={() => setHoverCategory(cat)}
              onMouseLeave={() => setHoverCategory(null)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors ${
                lockedCategory === cat ? "text-foreground bg-accent" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className={`w-2.5 h-2.5 rounded-sm ${categoryColorMap[cat]}`} />
              {categoryLabels[cat]}
            </button>
          ))}

        {colorMode === "phase" &&
          phaseLegend.map((item) => (
            <span key={item.label} className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
              {item.label}
            </span>
          ))}

        {colorMode === "block" &&
          blockLegend.map((item) => (
            <span key={item.label} className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
              {item.label}
            </span>
          ))}

        {colorMode === "electronegativity" && (
          <GradientLegend lowLabel="Low (0.7)" highLabel="High (4.0)" lowColor="hsl(240,70%,50%)" highColor="hsl(0,70%,50%)" />
        )}

        {colorMode === "radius" && (
          <GradientLegend lowLabel="Small" highLabel="Large" lowColor="hsl(0,70%,50%)" highColor="hsl(240,70%,50%)" />
        )}

        {colorMode === "discovery" && (
          <GradientLegend lowLabel="Ancient" highLabel="Modern" lowColor="hsl(240,65%,50%)" highColor="hsl(40,65%,50%)" />
        )}
      </div>

      {/* Grid */}
      <div className="overflow-x-auto -mx-4 px-4 pb-4">
        <div
          ref={gridRef}
          className="grid gap-1.5"
          style={{
            gridTemplateColumns: "repeat(18, minmax(48px, 1fr))",
            gridTemplateRows: "repeat(10, minmax(0, auto))",
            minWidth: "900px",
          }}
          tabIndex={0}
          onFocus={handleGridFocus}
          role="grid"
          aria-label="Periodic table of elements"
        >
          <button
            onMouseEnter={() => colorMode === "category" && setHoverCategory("lanthanide")}
            onMouseLeave={() => setHoverCategory(null)}
            onClick={() => colorMode === "category" && toggleLock("lanthanide")}
            className={`flex items-center justify-center text-[9px] rounded-sm border border-tile-border bg-tile-bg cursor-pointer hover:bg-tile-hover hover:text-foreground hover:scale-110 hover:z-10 hover:shadow-lg transition-all duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              lockedCategory === "lanthanide" ? "text-foreground ring-2 ring-primary" : "text-muted-foreground"
            }`}
            style={{ gridRow: 6, gridColumn: 3 }}
            aria-label="Lanthanides, elements 57 through 71"
          >
            57-71
          </button>
          <button
            onMouseEnter={() => colorMode === "category" && setHoverCategory("actinide")}
            onMouseLeave={() => setHoverCategory(null)}
            onClick={() => colorMode === "category" && toggleLock("actinide")}
            className={`flex items-center justify-center text-[9px] rounded-sm border border-tile-border bg-tile-bg cursor-pointer hover:bg-tile-hover hover:text-foreground hover:scale-110 hover:z-10 hover:shadow-lg transition-all duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              lockedCategory === "actinide" ? "text-foreground ring-2 ring-primary" : "text-muted-foreground"
            }`}
            style={{ gridRow: 7, gridColumn: 3 }}
            aria-label="Actinides, elements 89 through 103"
          >
            89-103
          </button>

          {elements.map((el) => (
            <ElementTile
              key={el.number}
              element={el}
              onClick={handleClick}
              dimmed={highlightCategory !== null && el.category !== highlightCategory}
              colorMode={colorMode}
              focused={focusedNumber === el.number}
            />
          ))}
        </div>
      </div>

      {selectedElement && (
        <ElementDetailPanel element={selectedElement} onClose={() => setSelectedElement(null)} />
      )}
    </div>
  );
}
